import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import bannerAuth from '@/assets/bannerAuth.jpg';
import logoYonyon from '@/assets/logoYonyon.png';

import { authForgotPassword } from '@/services/authService';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        setMessage(null);
        setError(null);

        const normalizedEmail = email.trim().toLowerCase();

        if (!normalizedEmail) {
            setError('Renseigne ton adresse email.');
            return;
        }

        setSubmitting(true);

        try {
            const result = await authForgotPassword(normalizedEmail);

            setMessage(result.message);
        } catch (err: unknown) {
            const msg =
                err instanceof Error
                    ? err.message
                    : 'Une erreur est survenue. Réessaie dans quelques instants.';

            setError(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-background-100 p-4 sm:p-6 lg:p-8">
            <div className="w-full max-w-6xl mx-auto flex flex-col lg:flex-row gap-6 lg:h-[580px]">

                {/* =========================
            PANNEAU IMAGE / BRANDING
        ========================== */}
                <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden rounded-2xl">
                    <img
                        src={bannerAuth}
                        alt="Yonyon, la communauté locale autour de Dole"
                        className="absolute inset-0 w-full h-full object-cover object-center"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/35 to-foreground-950/10" />

                    {/* Logo */}
                    <Link
                        to="/"
                        className="absolute top-6 left-1/2 -translate-x-1/2 z-10 cursor-pointer"
                    >
                        <img
                            src={logoYonyon}
                            alt="Yonyon"
                            className="h-24 w-auto object-contain"
                        />
                    </Link>

                    {/* Signature */}
                    <div
                        className="
                            absolute
                            top-32
                            left-1/2
                            -translate-x-1/2
                            z-10
                            w-full
                            max-w-[300px]
                            flex
                            flex-col
                            items-center
                            text-center
                            pointer-events-none
                        "
                    >
                        <p
                            className="
                                font-heading
                                text-lg
                                italic
                                font-medium
                                leading-snug
                                text-white
                                drop-shadow-sm
                            "
                        >
                            Une application qui te reconnecte
                            <br />
                            avec les gens autour de toi
                        </p>

                        <svg
                            viewBox="0 0 160 28"
                            className="mt-2 w-24 h-auto text-primary-500"
                            aria-hidden="true"
                        >
                            <path
                                d="M5 20 C45 29, 105 22, 155 5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="4"
                                strokeLinecap="round"
                            />
                        </svg>
                    </div>

                    <div className="relative w-full flex flex-col justify-end p-10">
                        <h1 className="mt-6 font-heading text-4xl font-semibold text-white leading-tight max-w-md">
                            On s'occupe de ton compte.
                        </h1>

                        <p className="mt-3 text-base text-white/85 max-w-md">
                            Entre l' adresse email associé à ton compte et tu recevras un lien pour choisir un
                            nouveau mot de passe.
                        </p>

                        <div className="mt-6 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                                <i className="ri-lock-line" />
                                Compte sécurisé
                            </span>

                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/20 backdrop-blur-sm text-white text-xs font-medium">
                                <i className="ri-mail-line" />
                                Lien par email
                            </span>
                        </div>
                    </div>
                </div>

                {/* =========================
            PANNEAU FORMULAIRE
        ========================== */}
                <div className="flex-1 flex items-center justify-center min-h-0">
                    <div className="w-full max-w-md">

                        {/* Logo mobile */}
                        <Link
                            to="/"
                            className="lg:hidden flex items-center justify-center mb-8 cursor-pointer"
                        >
                            <img
                                src={logoYonyon}
                                alt="Yonyon"
                                className="h-24 w-auto object-cover"
                            />
                        </Link>

                        <div className="bg-white rounded-2xl shadow-sm border border-background-100/50 p-6">

                            <h2 className="font-heading text-2xl font-semibold text-foreground-950">
                                Mot de passe oublié ?
                            </h2>

                            <p className="mt-1 text-sm text-foreground-500">
                                Entre l'adresse email associé à ton compte et nous t'enverrons un lien pour
                                réinitialiser ton mot de passe.
                            </p>

                            <form
                                onSubmit={handleSubmit}
                                className="mt-5 flex flex-col gap-3"
                            >
                                <div>
                                    <label
                                        htmlFor="forgot-email"
                                        className="block text-sm font-medium text-foreground-700 mb-1.5"
                                    >
                                        Email
                                    </label>

                                    <div className="relative">
                                        <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-300 pointer-events-none" />

                                        <input
                                            id="forgot-email"
                                            name="email"
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="toi@exemple.fr"
                                            autoComplete="email"
                                            className="w-full pl-10 pr-3 py-2.5 rounded-md border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-primary-50 border border-primary-200 text-primary-700 text-sm">
                                        <i className="ri-error-warning-line mt-0.5" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                {message && (
                                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-md bg-accent-50 border border-accent-100 text-accent-700 text-sm">
                                        <i className="ri-mail-check-line mt-0.5" />
                                        <span>{message}</span>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full px-4 py-3 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {submitting && (
                                        <i className="ri-loader-4-line animate-spin" />
                                    )}

                                    Envoyer le lien de réinitialisation
                                </button>
                            </form>
                        </div>

                        {/* Retour connexion */}
                        <Link
                            to="/auth"
                            className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
                        >
                            <i className="ri-arrow-left-line" />
                            Retour à la connexion
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}